/**
 * Represents a strategy for serializing/deserializing a value to/from URL
 */
export interface UrlValueStrategy<T> {
  serialize: (value: T) => string;
  deserialize: (value: string) => T;
  shouldUpdate: (value: T) => boolean;
}

/**
 * Configuration for a single URL parameter sync
 */
export interface UrlParamConfig<T> {
  /** The URL parameter key */
  paramKey: string;
  /** Function to get the current value from store */
  getValue: () => T;
  /** Function to set the value in store */
  setValue: (value: T) => void;
  /** Strategy for handling value conversion */
  strategy: UrlValueStrategy<T>;
}

/**
 * Built-in strategies for common types
 */
export const urlStrategies = {
  string: {
    serialize: (value: string) => value,
    deserialize: (value: string) => value,
    shouldUpdate: (value: string) => value.length > 0
  },

  json: {
    serialize: (value: unknown) => encodeURIComponent(JSON.stringify(value)),
    deserialize: (value: string) => {
      // Robust deserialization handling single- or double-encoded JSON
      const tryParse = (s: string) => {
        try {
          return JSON.parse(s);
        } catch {
          return null as unknown as never;
        }
      };

      // 1) decode once and try
      const once = decodeURIComponent(value);
      let parsed = tryParse(once);
      if (parsed !== null && parsed !== undefined) return parsed;

      // 2) if still encoded (e.g., starts with %5B or %7B), decode twice
      if (/^%5B|^%7B/i.test(value)) {
        const twice = decodeURIComponent(once);
        parsed = tryParse(twice);
        if (parsed !== null && parsed !== undefined) return parsed;
      }

      // 3) Last resort: try raw string if it already looks like JSON
      const trimmed = (value || '').toString().trim();
      if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
        parsed = tryParse(trimmed);
        if (parsed !== null && parsed !== undefined) return parsed;
      }

      // If all attempts fail, throw to surface the issue to caller
      throw new Error('Failed to deserialize JSON URL param');
    },
    shouldUpdate: (value: unknown) => {
      if (Array.isArray(value)) return value.length > 0;
      if (typeof value === 'object' && value !== null) return Object.keys(value).length > 0;
      return value !== null && value !== undefined;
    }
  },

  number: {
    serialize: (value: number) => value.toString(),
    deserialize: (value: string) => Number(value),
    shouldUpdate: (value: number) => !isNaN(value)
  },

  boolean: {
    serialize: (value: boolean) => value.toString(),
    deserialize: (value: string) => value === 'true',
    shouldUpdate: (value: boolean) => value !== false
  }
} as const;

// Compact facets CSV strategy
// Format: "brand:foo,bar;related-topics:baz"
// Only sectionIds in allowedSections are considered.
export type MinimalFacet = {
  id: string;
  sectionId: 'brand' | 'related-topics';
  name?: string;
  value?: string;
  count?: number;
  sectionName?: string;
};

const allowedSections = new Set<MinimalFacet['sectionId']>(['brand', 'related-topics']);

const sanitizeId = (raw: string): string => {
  const trimmed = (raw || '').trim();
  const safe = trimmed.replace(/[^A-Za-z0-9_-]/g, '');
  return safe;
};

const getSectionName = (sectionId: MinimalFacet['sectionId']): string => {
  if (sectionId === 'brand') return 'Brand';
  return 'Related Topics';
};

export const facetsCsvStrategy = {
  serialize: (value: MinimalFacet[]): string => {
    if (!Array.isArray(value) || value.length === 0) return '';
    const sectionToIds = new Map<MinimalFacet['sectionId'], string[]>();
    for (const facet of value) {
      if (!facet || !facet.id || !allowedSections.has(facet.sectionId)) continue;
      const list = sectionToIds.get(facet.sectionId) ?? [];
      list.push(sanitizeId(facet.id));
      sectionToIds.set(facet.sectionId, list);
    }
    const parts: string[] = [];
    sectionToIds.forEach((ids, section) => {
      if (ids.length > 0) parts.push(`${section}:${ids.join(',')}`);
    });
    return parts.join(';');
  },
  deserialize: (value: string): MinimalFacet[] => {
    if (!value) return [];
    const result: MinimalFacet[] = [];
    const groups = value
      .split(';')
      .map(g => g.trim())
      .filter(Boolean);
    for (const group of groups) {
      const [rawSection, rawIds] = group.split(':');
      const sectionId = (rawSection || '').trim() as MinimalFacet['sectionId'];
      if (!allowedSections.has(sectionId)) continue;
      const ids = (rawIds || '')
        .split(',')
        .map(id => sanitizeId(id))
        .filter(Boolean);
      for (const id of ids) {
        result.push({
          id,
          sectionId,
          name: id,
          value: id,
          count: 0,
          sectionName: getSectionName(sectionId)
        });
      }
    }
    return result;
  },
  shouldUpdate: (value: MinimalFacet[]): boolean => Array.isArray(value) && value.length > 0
} as const;

// Augment built-in strategies with facetsCsv without breaking existing imports
export const extendedUrlStrategies = {
  ...urlStrategies,
  facetsCsv: facetsCsvStrategy
} as const;
